import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Select, Spin, Table, Tooltip, message } from "antd";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, KeyRound, LockKeyhole, LockKeyholeOpen, RefreshCw } from "lucide-react";
import {
  AUTH_TOKEN,
  REACT_APP_API,
  ORG_ID,
  DOMAIN_ID,
} from "../../constants";
import { formatTimeByTimezone } from "../../common/formatTimebyTimezone";
import { AN_COLORS } from "../../design-system/tokens";
import { isAdminUser } from "../../utils/helpers";
import { listManagedPasswords, rotatePasswordNow, setPasswordManaged } from "../../service/dbVaultService";
import RevealPasswordDialog from "./RevealPasswordDialog";
import {
  actionFailureText,
  anyInFlight,
  failureTooltip,
  indexAccounts,
  isManaged,
  joinRow,
  managedSummary,
  passwordState,
} from "./dbVaultModel";
import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  SquareBadge,
  StatusPill,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../design-system/tableUi";

// Poll the managed-password list this often while any password is being
// taken over or rotated, and for a while after an action (the agent picks a
// change up on its next sync, usually within a minute).
const PASSWORD_POLL_MS = 10e3;
const AFTER_ACTION_POLL_MS = 90e3;
const QUEUED_TOAST = "Password change queued. It takes effect within a minute.";

const PasswordCell = ({ account }) => {
  const st = passwordState(account);
  if (st.kind === "pending" || st.kind === "rotating") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", color: AN_COLORS.ink3, fontSize: "0.8125rem" }}>
        <Spin size="small" />
        {st.label}
      </span>
    );
  }
  if (st.kind === "failed") {
    return (
      <Tooltip title={failureTooltip(account)}>
        <span style={{ cursor: "help" }}>
          <StatusPill tone="danger" label={st.label} />
        </span>
      </Tooltip>
    );
  }
  if (st.kind === "active") {
    const summary = managedSummary(account);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem", alignItems: "flex-start" }}>
        <StatusPill tone="success" label={st.label} />
        {summary && <span style={{ color: AN_COLORS.muted, fontSize: "0.75rem", whiteSpace: "nowrap" }}>{summary}</span>}
      </div>
    );
  }
  return <StatusPill tone="neutral" label={st.label} />;
};

const UserScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(1);

  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [appliedRole, setAppliedRole] = useState(null);

  const [databaseList, setDatabaseList] = useState([]);
  const [userList, setUserList] = useState([]);

  const [userData, setUserData] = useState([]);
  const [notification, contextHolder] = message.useMessage();
  const [modal, modalHolder] = Modal.useModal();

  // Password management is admin only (the list answers 403 to anyone else).
  const isAdmin = isAdminUser();
  const [accounts, setAccounts] = useState(null); // null until loaded
  const [hostIdByAddress, setHostIdByAddress] = useState({});
  const [reveal, setReveal] = useState(null); // { hostId, role, host }
  const pollAfterActionUntil = useRef(0);
  const pollTimer = useRef(null);
  const [, setTick] = useState(0);

  const PAGESIZE = 10;
  const [paginationParams, setPaginationParams] = useState({ current: 1, pageSize: PAGESIZE });

  useEffect(() => {
    fetchFilterLists();
    if (location.state && location.state.databaseFilter) {
      setSelectedUser(location.state.userFilter);
      setSelectedDatabase(location.state.databaseFilter);
    }
  }, [location.state]);

  useEffect(() => {
    fetchUserData();
  }, [selectedDatabase, selectedUser, appliedRole, paginationParams]);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await listManagedPasswords();
      setAccounts(Array.isArray(res?.accounts) ? res.accounts : []);
    } catch (err) {
      // 403 for a role this session can't manage, 404 on a server without
      // password management: the column stays hidden.
      if (err?.response?.status !== 403 && err?.response?.status !== 404) {
        console.error("listManagedPasswords failed:", err?.response?.status, err?.message);
      }
      // Keep what was shown; a fresh array keeps polling going.
      setAccounts((prev) => (prev ? [...prev] : prev));
    }
  }, []);

  // The users list doesn't always carry the row's db_hosts id; map host
  // addresses to ids so an unmanaged role can still be taken over.
  const fetchHostIds = useCallback(async () => {
    try {
      const res = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/listDbHosts`,
        { orgId: ORG_ID(), tenantId: DOMAIN_ID(), filters: [], requestId: "", limit: 500, page_id: 1 },
        { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } },
      );
      const map = {};
      (res?.data?.data || []).forEach((h) => {
        const addr = String(h?.host_vm_ip ?? h?.hostVmIp ?? "").trim();
        if (addr && h?.id != null && map[addr] == null) map[addr] = Number(h.id);
      });
      setHostIdByAddress(map);
    } catch (err) {
      console.error("listDbHosts failed:", err?.response?.status, err?.message);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchAccounts();
    fetchHostIds();
  }, [isAdmin, fetchAccounts, fetchHostIds]);

  // Poll while any password is in flight, or shortly after an action.
  useEffect(() => {
    if (!isAdmin || accounts === null) return undefined;
    const wanted = anyInFlight(accounts) || Date.now() < pollAfterActionUntil.current;
    if (!wanted) return undefined;
    pollTimer.current = setTimeout(fetchAccounts, PASSWORD_POLL_MS);
    return () => clearTimeout(pollTimer.current);
  }, [accounts, isAdmin, fetchAccounts]);

  // Keep "rotated 2 h ago" fresh without refetching.
  useEffect(() => {
    if (!accounts || accounts.length === 0) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 60e3);
    return () => clearInterval(t);
  }, [accounts]);

  const accountIndex = useMemo(() => indexAccounts(accounts || []), [accounts]);

  const afterAction = (toast) => {
    pollAfterActionUntil.current = Date.now() + AFTER_ACTION_POLL_MS;
    notification.success({ content: toast });
    fetchAccounts();
  };

  const failAction = (err, op) => {
    const status = err?.response?.status;
    notification.error({ content: actionFailureText(status, op) });
    if (status !== 403) fetchAccounts();
  };

  const confirmManage = ({ hostId, role }, host) =>
    modal.confirm({
      title: `Let AuthNull manage ${role}'s password on ${host || "this host"}?`,
      content:
        "AuthNull will set a new password now and rotate it on your schedule. Anyone or anything using the current password (scripts, apps, people) will be locked out. Connections through AuthNull keep working.",
      okText: "Take over password",
      cancelText: "Cancel",
      onOk: () =>
        setPasswordManaged({ hostId, role, enabled: true })
          .then(() => afterAction(QUEUED_TOAST))
          .catch((err) => failAction(err, "manage")),
    });

  const confirmStopManaging = ({ hostId, role }, host) =>
    modal.confirm({
      title: `Stop managing ${role}'s password on ${host || "this host"}?`,
      content: "AuthNull will stop rotating this password. The current password stays in effect.",
      okText: "Stop managing",
      cancelText: "Cancel",
      onOk: () =>
        setPasswordManaged({ hostId, role, enabled: false })
          .then(() => afterAction("AuthNull stopped managing this password."))
          .catch((err) => failAction(err, "manage")),
    });

  const rotateNow = ({ hostId, role }) =>
    rotatePasswordNow({ hostId, role })
      .then(() => afterAction(QUEUED_TOAST))
      .catch((err) => failAction(err, "rotate"));

  const passwordMenuItems = (record) => {
    if (!isAdmin || accounts === null) return [];
    const joined = joinRow(record, accountIndex, hostIdByAddress);
    const { hostId, role, account } = joined;
    if (!role) return [];
    const host = account?.host || record.host;
    if (!isManaged(account)) {
      return [
        {
          key: "managePassword",
          icon: <LockKeyhole size={13} />,
          label: "Manage password",
          disabled: hostId == null,
          title: hostId == null ? "AuthNull can't place this host yet." : undefined,
          onClick: () => confirmManage({ hostId, role }, host),
        },
      ];
    }
    const kind = passwordState(account).kind;
    const busy = kind === "pending" || kind === "rotating";
    return [
      {
        key: "rotateNow",
        icon: <RefreshCw size={13} />,
        label: "Rotate now",
        disabled: busy,
        onClick: () => rotateNow({ hostId, role }),
      },
      account.hasPassword && {
        key: "revealPassword",
        icon: <Eye size={13} />,
        label: "Reveal password",
        disabled: busy,
        onClick: () => setReveal({ hostId, role, host }),
      },
      { type: "divider" },
      {
        key: "stopManaging",
        danger: true,
        icon: <LockKeyholeOpen size={13} />,
        label: "Stop managing password",
        onClick: () => confirmStopManaging({ hostId, role }, host),
      },
    ].filter(Boolean);
  };

  const fetchFilterLists = async () => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
      };

      const dbRes = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueDatabasenames`,
        payload,
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );

      // Fetch unique user names
      const userRes = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueUsernames`,
        payload,
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );

      setDatabaseList(dbRes.data?.db_names || []);
      setUserList(userRes.data?.usernames || []);
    } catch (error) {
      console.error("Error fetching filter lists:", error?.response?.status, error?.message);
      notification.error({
        content: "Unable to fetch database/user lists",
      });
    }
  };

  const fetchUserData = () => {
    const filters = [];
    if (selectedDatabase)
      filters.push({ filterParameter: "Database", filterValue: selectedDatabase.split(" (")[0] });
    if (selectedUser) filters.push({ filterParameter: "User", filterValue: selectedUser });
    if (appliedRole) filters.push({ filterParameter: "Role", filterValue: appliedRole });

    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      filters,
      requestId: "",
      limit: paginationParams.pageSize,
      page_id: paginationParams.current,
    };

    setIsLoading(true);

    axios
      .post(`${REACT_APP_API}/api/v1/databaseService/listUser`, payload, {
        headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
      })
      .then((res) => {
        setIsLoading(false);
        setUserData(
          (res?.data?.data || []).map((item) => ({
            ...item,
            id: item.id,
          }))
        );
        setTotalCount(res?.data?.total_count || 0);
      })
      .catch((err) => {
        setIsLoading(false);
        notification.error({ content: "Unable to fetch users" });
        console.error("fetchUserData failed:", err?.response?.status, err?.message);
      });
  };

  const columns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (text) => (
        <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{text || "—"}</span>
      ),
    },
    {
      title: "Database",
      dataIndex: "db_name",
      key: "db_name",
      render: (text) => text || "—",
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (text) => <SquareBadge tone={text === "Admin" ? "warn" : undefined}>{text}</SquareBadge>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (text) => <StatusPill status={text} />,
    },
    isAdmin &&
      accounts !== null && {
        title: "Password",
        key: "password",
        width: 230,
        render: (_, record) => <PasswordCell account={joinRow(record, accountIndex, hostIdByAddress).account} />,
      },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (text) => (
        <MonoCell>{formatTimeByTimezone(text, localStorage.getItem("timezone"))}</MonoCell>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      align: "right",
      render: (_, record) => (
        <RowActionMenu
          items={(() => {
            const pw = passwordMenuItems(record);
            return [
              {
                key: "viewPrivileges",
                icon: <KeyRound size={13} />,
                label: "View privileges",
                onClick: () =>
                  navigate("/pam/databases/userPrivileges", {
                    state: { userFilter: record.username },
                  }),
              },
              ...(pw.length ? [{ type: "divider" }, ...pw] : []),
            ];
          })()}
        />
      ),
    },
  ].filter(Boolean);

  const resetPage = () => setPaginationParams({ current: 1, pageSize: PAGESIZE });

  const appliedFilters = [
    selectedDatabase && {
      key: "Database",
      label: `Database · ${selectedDatabase}`,
      clear: () => {
        setSelectedDatabase(null);
        resetPage();
      },
    },
    selectedUser && {
      key: "User",
      label: `User · ${selectedUser}`,
      clear: () => {
        setSelectedUser(null);
        resetPage();
      },
    },
    appliedRole && {
      key: "Role",
      label: `Role · ${appliedRole}`,
      clear: () => {
        setAppliedRole(null);
        resetPage();
      },
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setSelectedDatabase(null);
    setSelectedUser(null);
    setAppliedRole(null);
    resetPage();
  };

  return (
    <PageShell>
      {contextHolder}
      {modalHolder}
      {reveal && (
        <RevealPasswordDialog
          open
          hostId={reveal.hostId}
          role={reveal.role}
          host={reveal.host}
          onClose={() => setReveal(null)}
        />
      )}

      <PageHeader
        eyebrow="Admin · Database · Users"
        title="Database Users"
        subtitle="Accounts discovered on your databases, with their role and MFA enrollment status."
        chips={<CountChip count={totalCount} label={totalCount === 1 ? "User" : "Users"} />}
      />

      <FilterCard
        columns="1.6fr 1fr 1fr"
        applied={appliedFilters}
        onClearAll={clearFilters}
        showing={userData.length}
        total={totalCount}
      >
        <FilterField label="Database">
          <Select
            showSearch
            allowClear
            placeholder="Any database"
            style={{ width: "100%", height: 36 }}
            value={selectedDatabase}
            onChange={(value) => {
              setSelectedDatabase(value ?? null);
              resetPage();
            }}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={databaseList.map((db) => ({ label: db.split(" (")[0], value: db }))}
          />
        </FilterField>

        <FilterField label="User">
          <Select
            showSearch
            allowClear
            placeholder="Any user"
            style={{ width: "100%", height: 36 }}
            value={selectedUser}
            onChange={(value) => {
              setSelectedUser(value ?? null);
              resetPage();
            }}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={userList.map((user) => ({ label: user, value: user }))}
          />
        </FilterField>

        <FilterField label="Role">
          <Select
            allowClear
            placeholder="Any role"
            style={{ width: "100%", height: 36 }}
            value={appliedRole}
            onChange={(value) => {
              setAppliedRole(value ?? null);
              resetPage();
            }}
            options={[
              { value: "Admin", label: "Admin" },
              { value: "User", label: "User" },
            ]}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={userData}
          loading={isLoading}
          rowKey={(record) => record.id ?? `${record.username}-${record.db_name}-${record.host}`}
          locale={tableEmpty(
            isLoading,
            appliedFilters.length > 0
              ? "No users match your filters."
              : "No database users found yet.",
          )}
          pagination={{
            current: paginationParams.current,
            pageSize: PAGESIZE,
            total: totalCount,
            showSizeChanger: false,
            onChange: (page) => setPaginationParams({ current: page, pageSize: PAGESIZE }),
          }}
        />
      </TableCard>
    </PageShell>
  );
};

export default UserScreen;
